/**
 * Platform policy settings (Platform admin console, pages/PlatformAdmin.jsx,
 * docs/Decisions.md D53): the rules the owner set in D50 to D52 as values a
 * platform admin can change, either as the default for every organization or
 * for one organization.
 *
 * Each field either follows the layer below (the box "Use the ... default" is
 * ticked, and the inherited value is shown) or carries its own value. The
 * server keeps only the fields that are not inherited, so a later change to
 * the platform default still reaches every organization that has not
 * overridden that field.
 */
import { useCallback, useEffect, useState } from 'react';
import * as adminApi from '../api/admin';
import * as orgApi from '../api/organizations';
import { useConfirm } from '../lib/confirm';
import { useToast } from './toast';
import { ErrorAlert, Spinner, Field, SelectField, UserChip, formatDateTime } from './ui';
import {
  displayValue,
  formToLayer,
  layerToForm,
  policyFormErrors,
  rowsFor,
  sourceLabel,
} from '../lib/policySettings';
import { cn, ui } from '../lib/ui';

function PolicyRow({ row, entry, inheritLabel, inheritedValue, source, onChange, disabled }) {
  const key = `${row.section}.${row.field}`;
  const inputId = `policy-${key.replace('.', '-')}`;
  const set = patch => onChange(key, { ...entry, ...patch });

  let control;
  if (row.kind === 'bool') {
    control = (
      <label className={cn(ui.row, 'text-body-s')}>
        <input
          type="checkbox"
          checked={Boolean(entry.value)}
          disabled={disabled || entry.inherit}
          onChange={e => set({ value: e.target.checked })}
        />{' '}
        {row.label}
      </label>
    );
  } else if (row.kind === 'enum') {
    control = (
      <SelectField
        label={row.label}
        hint={row.hint}
        value={entry.value}
        disabled={disabled || entry.inherit}
        onChange={e => set({ value: e.target.value })}
      >
        {row.options.map(([value, label]) => (
          <option key={value} value={value}>
            {label}
          </option>
        ))}
      </SelectField>
    );
  } else {
    control = (
      <Field
        id={inputId}
        label={row.kind === 'money' ? `${row.label} (USD)` : row.label}
        hint={row.hint}
        type="number"
        inputMode="decimal"
        step={row.kind === 'int' ? 1 : 0.01}
        value={entry.value}
        disabled={disabled || entry.inherit}
        onChange={e => set({ value: e.target.value })}
      />
    );
  }

  return (
    <div className="mt-4">
      {control}
      {row.kind === 'bool' && row.hint ? (
        <p className={cn(ui.muted, 'text-caption')}>{row.hint}</p>
      ) : null}
      <label className={cn(ui.row, 'text-caption')}>
        <input
          type="checkbox"
          checked={entry.inherit}
          disabled={disabled}
          onChange={e =>
            onChange(key, {
              inherit: e.target.checked,
              value: e.target.checked ? inheritedValue.input : entry.value,
            })
          }
        />{' '}
        {inheritLabel}: {inheritedValue.text}
      </label>
      {source ? (
        <p className={cn(ui.muted, 'text-caption m-0')}>In effect now: {sourceLabel(source)}</p>
      ) : null}
    </div>
  );
}

export default function PolicySettingsPanel() {
  const toast = useToast();
  const confirm = useConfirm();

  const [scope, setScope] = useState('platform'); // 'platform' | 'org'
  const [orgs, setOrgs] = useState([]);
  const [orgId, setOrgId] = useState('');
  const [data, setData] = useState(null);
  const [form, setForm] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);

  const level = scope === 'org' ? 'org' : 'platform';

  useEffect(() => {
    orgApi
      .listOrganizations({ status: 'active', limit: 100 })
      .then(res => setOrgs(res.organizations || []))
      .catch(() => setOrgs([]));
  }, []);

  // What each field falls back to at this level.
  // For an org that is the platform default as resolved; for the platform,
  // the built-in defaults.
  const inherited = useCallback(
    d => (level === 'org' ? d.resolvedPlatformValues : d.defaults),
    [level]
  );

  // Loaded data carries the level it was loaded for, so a render between a
  // scope switch and the next load never pairs org rows with platform data.
  const apply = useCallback(
    d => {
      setData({ ...d, level });
      const own = level === 'org' ? d.override.layer : d.platform.layer;
      setForm(layerToForm(own, inherited(d), level));
    },
    [level, inherited]
  );

  const load = useCallback(() => {
    if (level === 'org' && !orgId) {
      setData(null);
      setForm(null);
      setLoading(false);
      return;
    }
    setLoading(true);
    setLoadError(null);
    setSaveError(null);
    const request =
      level === 'org'
        ? Promise.all([adminApi.getOrgPolicySettings(orgId), adminApi.getPolicySettings()]).then(
            ([org, platform]) => ({ ...org, resolvedPlatformValues: platform.resolved.values })
          )
        : adminApi.getPolicySettings();
    request
      .then(apply)
      .catch(err => setLoadError(err))
      .finally(() => setLoading(false));
  }, [level, orgId, apply]);

  useEffect(() => {
    load();
  }, [load]);

  const ready = Boolean(form && data && data.level === level);
  const errors = ready ? policyFormErrors(form, data.fields, level) : [];
  const onChange = (key, entry) => setForm(current => ({ ...current, [key]: entry }));

  async function onSave(event) {
    event.preventDefault();
    if (errors.length) return;
    setSaving(true);
    setSaveError(null);
    try {
      const layer = formToLayer(form, level);
      if (level === 'org') {
        const org = await adminApi.putOrgPolicySettings(orgId, layer);
        apply({ ...org, resolvedPlatformValues: data.resolvedPlatformValues });
      } else {
        apply(await adminApi.putPolicySettings(layer));
      }
      toast.success(level === 'org' ? 'Organization settings saved.' : 'Platform defaults saved.');
    } catch (err) {
      setSaveError(err);
      toast.error(err);
    } finally {
      setSaving(false);
    }
  }

  async function onClear() {
    const ok = await confirm({
      title: "Clear this organization's settings?",
      message: 'Every setting goes back to the platform default for this organization.',
      confirmLabel: 'Clear settings',
      danger: true,
    });
    if (!ok) return;
    setSaving(true);
    try {
      const org = await adminApi.clearOrgPolicySettings(orgId);
      apply({ ...org, resolvedPlatformValues: data.resolvedPlatformValues });
      toast.success('This organization now follows the platform defaults.');
    } catch (err) {
      setSaveError(err);
      toast.error(err);
    } finally {
      setSaving(false);
    }
  }

  const row = level === 'org' ? data?.override : data?.platform;
  const sources = data?.resolved?.sources;
  const inheritLabel = level === 'org' ? 'Use the platform default' : 'Use the built-in default';
  const base = ready ? inherited(data) : null;
  const sections = [
    ['donations', 'Donations'],
    ['marketplace', 'Marketplace fees'],
    ['sites', 'Organization websites'],
    ['ribbons', 'Ribbons'],
  ];

  return (
    <div className={ui.card}>
      <p className={cn(ui.muted, 'text-body-s')}>
        Rules every organization follows unless you set something else for it. Changes reach the
        services within a minute and are audit-logged.
      </p>

      <fieldset className="mt-4">
        <legend className={ui.label}>Apply to</legend>
        <label className={cn(ui.row, 'text-body-s')}>
          <input
            type="radio"
            name="policy-scope"
            checked={scope === 'platform'}
            onChange={() => setScope('platform')}
          />{' '}
          All organizations (platform default)
        </label>
        <label className={cn(ui.row, 'text-body-s')}>
          <input
            type="radio"
            name="policy-scope"
            checked={scope === 'org'}
            onChange={() => setScope('org')}
          />{' '}
          One organization
        </label>
        {scope === 'org' ? (
          <SelectField label="Organization" value={orgId} onChange={e => setOrgId(e.target.value)}>
            <option value="">Choose an organization</option>
            {orgs.map(org => (
              <option key={org.orgId} value={org.orgId}>
                {org.name}
                {org.slug ? ` (${org.slug})` : ''}
              </option>
            ))}
          </SelectField>
        ) : null}
      </fieldset>

      {loading ? <Spinner /> : null}
      {loadError ? (
        <>
          <ErrorAlert error={loadError} />
          <button type="button" className={ui.btnSecondary} onClick={load}>
            Retry
          </button>
        </>
      ) : null}

      {!loading && !loadError && form && data && data.level === level ? (
        <form onSubmit={onSave} className="mt-4">
          <ErrorAlert error={saveError} />
          {sections.map(([section, title]) => (
            <fieldset key={section} className="mt-4">
              <legend className={ui.label}>{title}</legend>
              {rowsFor(level)
                .filter(r => r.section === section)
                .map(r => {
                  const key = `${r.section}.${r.field}`;
                  const value = base?.[r.section]?.[r.field];
                  return (
                    <PolicyRow
                      key={key}
                      row={r}
                      entry={form[key]}
                      inheritLabel={inheritLabel}
                      inheritedValue={{
                        text: displayValue(r, value),
                        input: layerToForm({ [r.section]: { [r.field]: value } }, base, level)[key]
                          .value,
                      }}
                      source={sources?.[r.section]?.[r.field]}
                      onChange={onChange}
                      disabled={saving}
                    />
                  );
                })}
            </fieldset>
          ))}

          {errors.length > 0 && (
            <ul className={cn(ui.alertError, 'mt-4 list-disc pl-8')}>
              {errors.map(error => (
                <li key={error}>{error}</li>
              ))}
            </ul>
          )}

          <div className={cn(ui.row, 'mt-4')}>
            <button type="submit" className={ui.btnPrimary} disabled={saving || errors.length > 0}>
              {saving
                ? 'Saving…'
                : level === 'org'
                  ? 'Save for this organization'
                  : 'Save defaults'}
            </button>
            {level === 'org' ? (
              <button type="button" className={ui.btnSecondary} disabled={saving} onClick={onClear}>
                {"Clear this organization's settings"}
              </button>
            ) : null}
          </div>

          {row && (row.updatedAt || row.updatedBy) ? (
            <p className={cn(ui.muted, 'text-body-s mt-4')}>
              Last updated {row.updatedAt ? formatDateTime(row.updatedAt) : 'never'}
              {row.updatedBy ? (
                <>
                  {' '}
                  by <UserChip userId={row.updatedBy} />
                </>
              ) : null}
            </p>
          ) : null}
        </form>
      ) : null}

      {!loading && scope === 'org' && !orgId ? (
        <p className={cn(ui.muted, 'text-body-s mt-4')}>
          Choose an organization to see and change its settings.
        </p>
      ) : null}
    </div>
  );
}
